import type { ProblemDetails } from '@proerp/shared';
import { configureFilePreview } from '@proerp/ui';
import { getStoredToken } from './auth';

/**
 * Basis-URL der API. Das Portal läuft unter einer eigenen Domain
 * (partner.probig.com), spricht aber dieselbe API — in Produktion über den
 * Reverse-Proxy unter `/api`, lokal über den Vite-Proxy (vite.config).
 */
const BASE_URL = import.meta.env.VITE_API_URL ?? '/api';

/**
 * Alle Portal-Endpunkte liegen unter diesem Präfix (ADR 0085). Der Pfad steht
 * hier EINMAL: `portalRequest` setzt ihn selbst davor, damit ein Aufruf gar
 * nicht erst versehentlich an einem internen Endpunkt landen kann.
 */
const PORTAL_PREFIX = '/v1/portal';

/** Fehler mit RFC-7807-Details (CLAUDE.md §8). */
export class PortalApiError extends Error {
  constructor(
    readonly status: number,
    readonly problem?: ProblemDetails,
  ) {
    super(problem?.detail?.trim() || problem?.title?.trim() || `Anfrage fehlgeschlagen (HTTP ${status})`);
    this.name = 'PortalApiError';
  }

  /** Sitzung abgelaufen oder Zugang entzogen — die Anmeldung muss neu erfolgen. */
  get isUnauthenticated(): boolean {
    return this.status === 401;
  }
}

export interface PortalRequestOptions extends Omit<RequestInit, 'body'> {
  body?: string;
  /** Anfragen VOR der Anmeldung (Login-Schritte) tragen noch kein Token. */
  anonymous?: boolean;
}

/**
 * Lädt einen Bericht mit dem Token und stößt den Browser-Download an.
 *
 * Warum nicht einfach ein `<a href>` auf den Endpunkt: Das Token steht im
 * localStorage, nicht in einem Cookie — ein normaler Seitenaufruf trüge keinen
 * `Authorization`-Header und liefe in ein 403. Also holen, als Blob verpacken,
 * klicken. Der Dateiname muss dabei explizit gesetzt werden, weil Blob-URLs
 * kein `Content-Disposition` tragen.
 */
export async function downloadPortalFile(path: string, filename: string): Promise<void> {
  const headers = new Headers();
  const token = getStoredToken();
  if (token) headers.set('Authorization', `Bearer ${token}`);

  const response = await fetch(`${BASE_URL}${PORTAL_PREFIX}${path}`, { headers });
  if (!response.ok) {
    let problem: ProblemDetails | undefined;
    try {
      problem = (await response.json()) as ProblemDetails;
    } catch {
      problem = undefined;
    }
    throw new PortalApiError(response.status, problem);
  }

  const url = URL.createObjectURL(await response.blob());
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = filename;
  anchor.rel = 'noopener';
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  // Erst nach dem Speichern freigeben — sonst bricht der Download in Safari ab.
  setTimeout(() => URL.revokeObjectURL(url), 60_000);
}

/**
 * Eine Anfrage an das Partnerportal. Hängt das Token an, wirft bei Fehlern
 * einen `PortalApiError` mit der Server-Meldung (RFC 7807) und liefert bei
 * 204 `undefined`.
 */
export async function portalRequest<T>(path: string, options: PortalRequestOptions = {}): Promise<T> {
  const { anonymous = false, headers, ...rest } = options;
  const requestHeaders = new Headers(headers);
  if (options.body && !requestHeaders.has('Content-Type')) {
    requestHeaders.set('Content-Type', 'application/json');
  }
  if (!anonymous) {
    const token = getStoredToken();
    if (token) requestHeaders.set('Authorization', `Bearer ${token}`);
  }

  const response = await fetch(`${BASE_URL}${PORTAL_PREFIX}${path}`, {
    ...rest,
    headers: requestHeaders,
  });

  if (!response.ok) {
    let problem: ProblemDetails | undefined;
    try {
      problem = (await response.json()) as ProblemDetails;
    } catch {
      problem = undefined;
    }
    throw new PortalApiError(response.status, problem);
  }
  if (response.status === 204) {
    return undefined as T;
  }
  return (await response.json()) as T;
}

/**
 * Holt eine Portal-Datei als **rohe Bytes** — für die Vorschau (ADR 0075).
 *
 * pdf.js bekommt die Bytes direkt, NICHT eine `blob:`-URL: Eine Blob-URL müsste
 * pdf.js selbst laden, was in Safari aus dem Worker heraus scheitert und in
 * Produktion zusätzlich am CSP (`connect-src 'self'`) abprallt.
 */
export async function fetchPortalBytes(path: string): Promise<Uint8Array> {
  const headers = new Headers();
  const token = getStoredToken();
  if (token) headers.set('Authorization', `Bearer ${token}`);

  const response = await fetch(`${BASE_URL}${PORTAL_PREFIX}${path}`, { headers });
  if (!response.ok) {
    let problem: ProblemDetails | undefined;
    try {
      problem = (await response.json()) as ProblemDetails;
    } catch {
      problem = undefined;
    }
    throw new PortalApiError(response.status, problem);
  }
  return new Uint8Array(await response.arrayBuffer());
}

/**
 * Nutzerlesbarer Text für einen beliebigen Fehlerwert. `PortalApiError` trägt
 * bereits die Server-Begründung (RFC 7807) in `message` — die reicht durch.
 */
export function describePortalError(error: unknown): string {
  if (error instanceof Error && error.message) return error.message;
  return 'Unbekannter Fehler.';
}

/**
 * Die Datei-Vorschau aus `@proerp/ui` bekommt hier ihren Zugang zur API.
 * Einmal beim Laden des Moduls — `apiClient` steckt in jedem Datenpfad des
 * Portals, damit ist `openFilePreview()` überall verdrahtet, ohne dass eine
 * Seite einen Provider um sich braucht.
 */
configureFilePreview({
  loadBytes: fetchPortalBytes,
  download: downloadPortalFile,
  formatError: describePortalError,
});

/**
 * Lädt eine Datei ins Portal hoch (ADR 0087) — Rohbytes im Body, Metadaten im
 * Pfad, wie überall im ERP. `Content-Type` ist der echte Typ der Datei: Der
 * Server prüft ihn gegen seine Positivliste, `multipart` würde ihn nur
 * verstecken.
 */
export async function uploadPortalFile<T>(path: string, file: File): Promise<T> {
  const headers = new Headers({ 'Content-Type': file.type || 'application/octet-stream' });
  const token = getStoredToken();
  if (token) headers.set('Authorization', `Bearer ${token}`);

  const response = await fetch(`${BASE_URL}${PORTAL_PREFIX}${path}`, {
    method: 'POST',
    body: file,
    headers,
  });
  if (!response.ok) {
    let problem: ProblemDetails | undefined;
    try {
      problem = (await response.json()) as ProblemDetails;
    } catch {
      problem = undefined;
    }
    throw new PortalApiError(response.status, problem);
  }
  return (await response.json()) as T;
}
